import { useCallback, useMemo, useRef, useState } from 'react';
import {
  cancelAnimation,
  Easing,
  Extrapolation,
  interpolate,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { scheduleOnRN } from 'react-native-worklets';

import type { FamilyEvent } from '@/features/guardian/family-events';

const OPEN_DURATION = 280;
const CLOSE_DURATION = 220;
const EASING = Easing.out(Easing.cubic);

const CLOSED_RATIOS = { calendar: 2.05, day: 1 } as const;
const OPEN_RATIOS = { calendar: 1.55, day: 1, detail: 1.25 } as const;

export type EventDetailPanelWidths = {
  calendarClosed: number;
  dayClosed: number;
  calendarOpen: number;
  dayOpen: number;
  detailOpen: number;
};

const EMPTY_WIDTHS: EventDetailPanelWidths = {
  calendarClosed: 0,
  dayClosed: 0,
  calendarOpen: 0,
  dayOpen: 0,
  detailOpen: 0,
};

export function computeEventDetailPanelWidths(
  containerWidth: number,
  columnGap: number,
): EventDetailPanelWidths {
  if (containerWidth <= 0) return EMPTY_WIDTHS;

  const closedUsableWidth = Math.max(0, containerWidth - columnGap);
  const closedUnit = closedUsableWidth / (CLOSED_RATIOS.calendar + CLOSED_RATIOS.day);
  const openUsableWidth = Math.max(0, containerWidth - columnGap * 2);
  const openUnit = openUsableWidth / (OPEN_RATIOS.calendar + OPEN_RATIOS.day + OPEN_RATIOS.detail);

  return {
    calendarClosed: closedUnit * CLOSED_RATIOS.calendar,
    dayClosed: closedUnit * CLOSED_RATIOS.day,
    calendarOpen: openUnit * OPEN_RATIOS.calendar,
    dayOpen: openUnit * OPEN_RATIOS.day,
    detailOpen: openUnit * OPEN_RATIOS.detail,
  };
}

export function useEventDetailPanel(containerWidth: number, columnGap: number) {
  const progress = useSharedValue(0);
  const reduceMotion = useReducedMotion();
  const [event, setEvent] = useState<FamilyEvent | null>(null);
  const isOpenRef = useRef(false);

  const widths = useMemo(
    () => computeEventDetailPanelWidths(containerWidth, columnGap),
    [columnGap, containerWidth],
  );

  const clearEvent = useCallback(() => {
    requestAnimationFrame(() => {
      if (!isOpenRef.current) setEvent(null);
    });
  }, []);

  const open = useCallback(
    (nextEvent: FamilyEvent) => {
      setEvent(nextEvent);
      if (isOpenRef.current) return;

      isOpenRef.current = true;
      cancelAnimation(progress);
      progress.value = withTiming(1, {
        duration: reduceMotion ? 0 : OPEN_DURATION,
        easing: EASING,
      });
    },
    [progress, reduceMotion],
  );

  const close = useCallback(() => {
    if (!isOpenRef.current) return;

    isOpenRef.current = false;
    cancelAnimation(progress);
    progress.value = withTiming(
      0,
      { duration: reduceMotion ? 0 : CLOSE_DURATION, easing: EASING },
      finished => {
        if (finished) scheduleOnRN(clearEvent);
      },
    );
  }, [clearEvent, progress, reduceMotion]);

  const calendarStyle = useAnimatedStyle(() => ({
    width: interpolate(progress.value, [0, 1], [widths.calendarClosed, widths.calendarOpen]),
  }));

  const dayPanelStyle = useAnimatedStyle(() => ({
    width: interpolate(progress.value, [0, 1], [widths.dayClosed, widths.dayOpen]),
  }));

  const detailStyle = useAnimatedStyle(() => ({
    width: interpolate(progress.value, [0, 1], [0, widths.detailOpen]),
    marginLeft: interpolate(progress.value, [0, 1], [0, columnGap]),
    opacity: interpolate(progress.value, [0.35, 1], [0, 1], Extrapolation.CLAMP),
    transform: [
      {
        translateX: interpolate(progress.value, [0, 1], [widths.detailOpen * 0.15, 0]),
      },
    ],
  }));

  return {
    event,
    open,
    close,
    widths,
    calendarStyle,
    dayPanelStyle,
    detailStyle,
  };
}
